/**
 * Phase 2 Test Panel
 *
 * Admin-only component for running Phase 2 integration tests.
 * Displays test results organized by category with pass/fail indicators.
 */

import { useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import {
  Play,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronRight,
  Loader2,
  RefreshCw,
  AlertCircle,
} from 'lucide-react'
import { AdminOnly } from '@/components/AdminOnly'
import { supabase } from '@/lib/supabase'
import { runPhase2TestsByCategory, type TestCategory, type TestResult } from '@/lib/testing/phase2Tests'
import type { Nation } from '@/types'

interface NationWithWorld extends Nation {
  world: {
    id: string
    name: string
  }
}

export function Phase2TestPanel() {
  const [selectedNationId, setSelectedNationId] = useState<string>('')
  const [testResults, setTestResults] = useState<TestCategory[] | null>(null)
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set())

  // Fetch all nations for selection
  const { data: nations, isLoading: nationsLoading } = useQuery({
    queryKey: ['nations', 'all', 'forTesting'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('nations')
        .select('*, world:worlds(id, name)')
        .order('name', { ascending: true })

      if (error) throw error
      return data as NationWithWorld[]
    },
  })

  // Run tests mutation
  const runTestsMutation = useMutation({
    mutationFn: async (nationId: string) => {
      return await runPhase2TestsByCategory(nationId)
    },
    onSuccess: (results) => {
      setTestResults(results)
      // Expand categories with failures by default
      const failedCategories = results.filter((c) => c.failed > 0).map((c) => c.name)
      setExpandedCategories(new Set(failedCategories.length > 0 ? failedCategories : results.map((c) => c.name)))
    },
  })

  const handleRunTests = () => {
    if (selectedNationId) {
      runTestsMutation.mutate(selectedNationId)
    }
  }

  const toggleCategory = (categoryName: string) => {
    const newExpanded = new Set(expandedCategories)
    if (newExpanded.has(categoryName)) {
      newExpanded.delete(categoryName)
    } else {
      newExpanded.add(categoryName)
    }
    setExpandedCategories(newExpanded)
  }

  const totalPassed = testResults?.reduce((sum, c) => sum + c.passed, 0) || 0
  const totalFailed = testResults?.reduce((sum, c) => sum + c.failed, 0) || 0
  const totalTests = totalPassed + totalFailed

  return (
    <AdminOnly fallback={<div className="p-4 text-slate-400">Admin access required</div>}>
      <div className="bg-slate-800 rounded-lg border border-slate-700">
        {/* Header */}
        <div className="p-4 border-b border-slate-700">
          <h2 className="text-lg font-semibold text-white">Phase 2 Integration Tests</h2>
          <p className="text-sm text-slate-400 mt-1">
            Verify cohorts, parties, candidates, and visualizations are working correctly.
          </p>
        </div>

        {/* Controls */}
        <div className="p-4 border-b border-slate-700 flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[200px]">
            <label htmlFor="nation-select" className="block text-sm font-medium text-slate-300 mb-1">
              Nation
            </label>
            <select
              id="nation-select"
              value={selectedNationId}
              onChange={(e) => setSelectedNationId(e.target.value)}
              disabled={nationsLoading || runTestsMutation.isPending}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select a nation...</option>
              {nations?.map((nation) => (
                <option key={nation.id} value={nation.id}>
                  {nation.name} ({nation.world.name})
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-end gap-2">
            <button
              onClick={handleRunTests}
              disabled={!selectedNationId || runTestsMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white rounded-md text-sm font-medium transition-colors"
            >
              {runTestsMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Running...
                </>
              ) : (
                <>
                  <Play className="h-4 w-4" />
                  Run All Tests
                </>
              )}
            </button>

            {testResults && (
              <button
                onClick={() => {
                  setTestResults(null)
                  setExpandedCategories(new Set())
                }}
                className="flex items-center gap-2 px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded-md text-sm font-medium transition-colors"
              >
                <RefreshCw className="h-4 w-4" />
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Summary */}
        {testResults && (
          <div className="p-4 border-b border-slate-700 flex items-center gap-6">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              <span className="text-green-400 font-medium">{totalPassed} passed</span>
            </div>
            <div className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-red-500" />
              <span className="text-red-400 font-medium">{totalFailed} failed</span>
            </div>
            <div className="text-slate-400">
              {totalTests} total tests
            </div>
            {totalFailed === 0 && totalTests > 0 && (
              <div className="ml-auto flex items-center gap-2 text-green-400">
                <CheckCircle className="h-5 w-5" />
                All tests passing!
              </div>
            )}
          </div>
        )}

        {/* Error Display */}
        {runTestsMutation.isError && (
          <div className="p-4 border-b border-slate-700 bg-red-900/20">
            <div className="flex items-center gap-2 text-red-400">
              <AlertCircle className="h-5 w-5" />
              <span>Error running tests: {(runTestsMutation.error as Error).message}</span>
            </div>
          </div>
        )}

        {/* Test Results */}
        {testResults && (
          <div className="divide-y divide-slate-700">
            {testResults.map((category) => (
              <TestCategorySection
                key={category.name}
                category={category}
                isExpanded={expandedCategories.has(category.name)}
                onToggle={() => toggleCategory(category.name)}
              />
            ))}
          </div>
        )}

        {/* Empty State */}
        {!testResults && !runTestsMutation.isPending && (
          <div className="p-8 text-center text-slate-400">
            <Play className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Select a nation and click "Run All Tests" to begin.</p>
          </div>
        )}
      </div>
    </AdminOnly>
  )
}

// ============================================================================
// Sub-components
// ============================================================================

interface TestCategorySectionProps {
  category: TestCategory
  isExpanded: boolean
  onToggle: () => void
}

function TestCategorySection({ category, isExpanded, onToggle }: TestCategorySectionProps) {
  const allPassed = category.failed === 0

  return (
    <div>
      {/* Category Header */}
      <button
        onClick={onToggle}
        className="w-full p-4 flex items-center gap-3 hover:bg-slate-700/50 transition-colors text-left"
      >
        {isExpanded ? (
          <ChevronDown className="h-5 w-5 text-slate-400" />
        ) : (
          <ChevronRight className="h-5 w-5 text-slate-400" />
        )}

        <span className="font-medium text-white flex-1">{category.name}</span>

        <span
          className={`text-sm font-medium ${
            allPassed ? 'text-green-400' : 'text-red-400'
          }`}
        >
          {category.passed}/{category.tests.length} passed
        </span>

        {allPassed ? (
          <CheckCircle className="h-5 w-5 text-green-500" />
        ) : (
          <XCircle className="h-5 w-5 text-red-500" />
        )}
      </button>

      {/* Category Tests */}
      {isExpanded && (
        <div className="pb-4 px-4">
          <div className="ml-8 space-y-2">
            {category.tests.map((test, index) => (
              <TestResultItem key={`${test.name}-${index}`} result={test} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

interface TestResultItemProps {
  result: TestResult
}

function TestResultItem({ result }: TestResultItemProps) {
  const [showErrors, setShowErrors] = useState(false)

  return (
    <div className={`rounded-md p-3 ${result.passed ? 'bg-slate-700/30' : 'bg-red-900/20'}`}>
      <div className="flex items-start gap-3">
        {result.passed ? (
          <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
        ) : (
          <XCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className={`font-medium ${result.passed ? 'text-slate-200' : 'text-red-300'}`}>
              {result.name}
            </span>
          </div>

          <p className="text-sm text-slate-400 mt-1">{result.details}</p>

          {result.errors && result.errors.length > 0 && (
            <div className="mt-2">
              <button
                onClick={() => setShowErrors(!showErrors)}
                className="text-sm text-red-400 hover:text-red-300 flex items-center gap-1"
              >
                {showErrors ? (
                  <>
                    <ChevronDown className="h-4 w-4" />
                    Hide errors ({result.errors.length})
                  </>
                ) : (
                  <>
                    <ChevronRight className="h-4 w-4" />
                    Show errors ({result.errors.length})
                  </>
                )}
              </button>

              {showErrors && (
                <ul className="mt-2 space-y-1 text-sm text-red-400 bg-red-900/30 rounded p-2">
                  {result.errors.map((error, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-red-500">•</span>
                      <span className="break-words">{error}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default Phase2TestPanel
